import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { homeFor, RoleKey, StaffContext, useStaffGate } from '../../lib/staffContext';
import { StaffShell, type NavItem } from '../../ui/StaffShell';
import { ContextError } from '../../ui/ContextError';
import { Pill } from '../../ui/DataBits';
import { bandOf } from '../../ui/score';
import { roleLabel } from '../../ui/roles';
import { useI18n } from '../../i18n';
import { headNav } from '../head/headNav';

// Shared pieces for release 3 (0019).

/** A cross-school window (municipal or period report). */
export type XsWindow = {
  status: 'ok' | 'too_few_schools' | 'insufficient' | 'insufficient_spread' | 'suppressed_near_unanimous' | 'hidden_complement';
  schools?: number;
  total?: number;
  mean?: number | null;
  mood_counts?: Record<string, number>;
  topic_shares?: Record<string, number | null>;
  min_schools?: number;
};
export type XsReport = {
  total: XsWindow;
  stages: Record<string, XsWindow>;
  from: string;
  to: string;
  period: string;
  level: 'country' | 'municipality';
  code: string;
};

export function XsCell({ w }: { w: XsWindow | undefined }) {
  const { t, num } = useI18n();
  if (!w) return <span className="text-muted">–</span>;
  if (w.status === 'ok' && w.mean != null) {
    const b = bandOf(w.mean);
    return (
      <span className="win-cell">
        <strong>{num(w.mean)}/5</strong> <Pill kind={b.key}>{t[`band.${b.key}`]}</Pill>
        <span className="text-muted"> · {t['r3.schoolsN'](w.schools ?? 0)} · {t['h2.n'](w.total ?? 0)}</span>
      </span>
    );
  }
  if (w.status === 'too_few_schools') return <span className="text-muted">{t['r3.tooFewSchools'](w.schools ?? 0)}</span>;
  return <span className="text-muted">{t[`r3.st.${w.status}` as 'r3.st.insufficient']}</span>;
}

export type Evidence = {
  schools_band: string;
  actions: number;
  mood_change: number;
  baseline_mood_change: number | null;
  topic_change: number | null;
  baseline_topic_change: number | null;
} | null;

const signed = (n: number, f: (x: number) => string) => (n > 0 ? `+${f(n)}` : n < 0 ? `−${f(-n)}` : f(0));

/** J3: the "what works" hint under a playbook activity. */
export function EvidenceHint({ e }: { e: Evidence | undefined }) {
  const { t, num } = useI18n();
  if (!e) return null;
  const mood = signed(e.mood_change, num);
  const base = e.baseline_mood_change === null ? null : signed(e.baseline_mood_change, num);
  return (
    <p className="evidence">
      <Pill kind="warn">{t['ts.draft']}</Pill> {t['r3.evidence'](e.schools_band, e.actions, mood)}
      {base !== null && <> {t['r3.evidenceBase'](base)}</>} <span className="text-muted">{t['r3.notCausal']}</span>
    </p>
  );
}

/**
 * Frame for pages any school-side role reaches (own check-in, calendar for
 * school admins): the role's home in the nav plus the page itself.
 */
export function StaffFrame({ roles, title, extraNav = [], children }: { roles: RoleKey[]; title: string; extraNav?: NavItem[]; children: (ctx: StaffContext) => ReactNode }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { ctx, error } = useStaffGate(roles);
  if (error) return <ContextError message={error} />;
  if (!ctx || !ctx.active_role) return <main className="page-loading" role="status">{t.loading}</main>;
  async function signOut() {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }
  return (
    <StaffShell
      role={roleLabel(t, ctx.active_role)}
      nav={
        ctx.active_role === 'head_of_school'
          ? headNav(t, extraNav.some((n) => n.href === '/me/checkin' && n.active) ? 'me' : 'overview')
          : [{ icon: '🏠', label: t['notif.home'], href: homeFor(ctx.active_role) }, ...extraNav]
      }
      title={title}
      who={ctx.display_name}
      ctx={ctx}
      onSignOut={signOut}
    >
      {children(ctx)}
    </StaffShell>
  );
}
